import { ampli } from 'lib-shared-tracking';
import { useCallback, useEffect } from 'react';

import { appActions } from 'app/app.actions';
import { websocketActions } from 'app/middleware/websocket/actions/websocket';
import { bindRtkActionToPromise } from 'app/utils/promiseActions';
import { notificationActions } from 'components/notifications/actions/notification';
import { presenceActions } from 'components/presence/actions/presenceActions';
import { debugLog } from 'utils/debugLog';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';

/**
 * A hook that listens to the network changes in order to react to the new
 * connectivity state accordingly in terms of updating the app state.
 *
 * Any app-wide code that depends on network change should be added here. For
 * example, refresh token pause and resume methods.
 */
export const useAppNetworkChange = (): void => {
  const dispatch = useAppDispatch();
  const appAsyncStatus = useAppSelector((state) => state.app.asyncStatus.app);
  const webSocketCreateAsyncStatus = useAppSelector(
    (state) => state.websocket.meta.asyncCreateStatus,
  );

  /** Handles the app going into an online state. */
  const handleOnline = useCallback(() => {
    debugLog('NETWORK-CONNECTIVITY', 'Online event received');
    ampli.networkConnectionStatusChanged({ status: 'online' });

    if (webSocketCreateAsyncStatus === 'rejected') {
      debugLog(
        'NETWORK-CONNECTIVITY',
        'Previous reconnect attempt failed, trying to reconnect from the online event',
      );

      bindRtkActionToPromise(dispatch, websocketActions.create)()
        .then(() => {
          ampli.websocketConnectionCreated({ source: 'online_event' });
          dispatch(appActions.hideModal({ type: 'app:networkConnectionLost' }));
        })
        .catch();
    }
  }, [dispatch, webSocketCreateAsyncStatus]);

  /** Handles the app going into an offline state. */
  const handleOffline = useCallback(() => {
    debugLog('NETWORK-CONNECTIVITY', 'Offline event received');
    ampli.networkConnectionStatusChanged({ status: 'offline' });

    dispatch(websocketActions.close());
    dispatch(presenceActions.presenceShowOffline());
    dispatch(
      notificationActions.notificationFaviconChange({ type: 'offline' }),
    );
    dispatch(
      appActions.showModal({ modal: { type: 'app:networkConnectionLost' } }),
    );
  }, [dispatch]);

  // Sets up the network change handlers as soon as the app is initialized.
  useEffect(() => {
    if (appAsyncStatus === 'fulfilled') {
      window.addEventListener('online', handleOnline);
      window.addEventListener('offline', handleOffline);

      return () => {
        window.removeEventListener('online', handleOnline);
        window.removeEventListener('offline', handleOffline);
      };
    }
  }, [appAsyncStatus, handleOnline, handleOffline]);
};
